import { baseApi } from '../../../core/baseApi';
import {
  createListQuery,
  createUpdateMutation,
  createDeleteMutation,
  unwrapApiResponse
} from '../../../patterns/crud';

// Types for bulk campaign
interface BulkCampaignRecipient {
  phoneNumber: string;
  contactId?: string;
}

interface CreateBulkCampaignArgs {
  data: {
    message: string;
    recipients: BulkCampaignRecipient[];
    metadata?: Record<string, unknown>;
  };
}

interface BulkCampaign {
  id: string;
  status: string;
  message: string;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
}

export interface BulkCampaignRecipientResult {
  id: number;
  phoneNumber: string;
  contactId: string | null;
  status: string;
  errorMessage: string | null;
}

export interface BulkCampaignDetail extends BulkCampaign {
  recipients?: BulkCampaignRecipientResult[];
}

export interface BulkCampaignProgress {
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  pending: number;
  progress: number;
}

export const bulkMessagingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBulkCampaigns: createListQuery('/bulk-messages/campaigns', 'BulkCampaigns')(builder),

    // Creates a bulk campaign - backend queues and processes all messages via BullMQ
    // NOTE: We intentionally do NOT invalidate Messages/Conversations here because
    // conversations are created asynchronously by the queue worker. Invalidating
    // immediately would cause a premature refetch before data exists.
    // The BulkMessageModal handles cache invalidation after polling for completion.
    createBulkCampaign: builder.mutation<BulkCampaign, CreateBulkCampaignArgs>({
      query: (args) => ({
        url: '/bulk-messages/campaigns',
        method: 'POST',
        body: args.data
      }),
      transformResponse: unwrapApiResponse<BulkCampaign>,
      invalidatesTags: [
        { type: 'BulkCampaigns', id: 'LIST' }
        // Messages and Conversations are NOT invalidated here - see note above
      ]
    }),

    // Get full campaign detail with recipients (for results view)
    getCampaignDetail: builder.query<BulkCampaignDetail, { campaignId: string | number }>({
      query: ({ campaignId }) => `/bulk-messages/campaigns/${campaignId}`,
      transformResponse: unwrapApiResponse<BulkCampaignDetail>,
    }),

    // Get campaign progress for polling
    getCampaignProgress: builder.query<BulkCampaignProgress, { campaignId: string | number }>({
      query: ({ campaignId }) => `/bulk-messages/campaigns/${campaignId}/progress`,
      transformResponse: unwrapApiResponse<BulkCampaignProgress>,
      // Don't cache progress - we want fresh data each poll
      keepUnusedDataFor: 0
    }),

    updateCampaignStatus: createUpdateMutation('/bulk-messages/campaigns', 'BulkCampaigns')(builder),
    cancelCampaign: createDeleteMutation('/bulk-messages/campaigns', 'BulkCampaigns')(builder)
  })
});

export const {
  useGetBulkCampaignsQuery,
  useCreateBulkCampaignMutation,
  useGetCampaignDetailQuery,
  useLazyGetCampaignDetailQuery,
  useGetCampaignProgressQuery,
  useLazyGetCampaignProgressQuery,
  useUpdateCampaignStatusMutation,
  useCancelCampaignMutation
} = bulkMessagingApi;
